<script lang="ts" setup>
const route = useRoute()
const { $posthog, $gtm, $intercom } = useNuxtApp()
const { workspace, activeWorkspaceId } = useAuth()
const { isMainSubscriptionPaused } = useSubscription()

if (route.name !== '2-search')
  usePusherProvider()

const subscriptionPausedCoverVisible = computed(() =>
  workspace.value?.attributes.isFrozen || isMainSubscriptionPaused.value,
)

const isSubscribeModalOpen = useSubscriptionModalOpenState()

watch(() => route.name, () => {
  // Close subscription modal if user naviagtes for example with back button to prevent user staying stuck in the modal
  if (isSubscribeModalOpen.value)
    isSubscribeModalOpen.value = false
})

const handleCheckoutStarted = () => {
  $posthog.capture('checkout_started', {
    is_upgrade: false,
  })
  $gtm.push({
    event: 'checkout_started',
    is_upgrade: false,
  })
}

const handlePlanSelected = (params?: PlanSelectedParams) => {
  if (!params)
    return

  $posthog.capture('plan_selected', {
    is_upgrade: false,
  })

  $gtm.push({
    event: 'add_to_cart',
    ecommerce: {
      currency: params.currency,
      value: params.price,
      items: [{
        item_id: params.priceId,
        item_name: params.plan,
        price: params.price,
        item_category: 'plan',
        quantity: 1,
      }],
    },
  })
}

const handleActivationCompleted = (params?: ActivationCompletedParams) => {
  if (!params)
    return

  $gtm.push({
    event: 'activateSubscription',
    accountType: 'company',
    planId: params.planPriceId,
  })

  $posthog.capture('subscription_created', {
    plan_id: params.planPriceId,
  })

  const transactionId = `${activeWorkspaceId.value}-${Date.now()}`

  $gtm.push({
    event: 'newTransaction',
    transactionId,
    transactionTotal: params.annualPrice,
    transactionProducts: [{
      name: params.plan,
      category: 'Plan',
      sku: params.planPriceId,
      price: params.annualPrice,
      currency: params.currency,
      quantity: 1,
    }],
  })

  $gtm.push({ ecommerce: null })

  $gtm.push({
    event: 'purchase',
    ecommerce: {
      transaction_id: transactionId,
      value: params.annualPrice,
      currency: params.currency,
      coupon: params.couponId,
      items: [{
        item_id: params.planPriceId,
        item_name: params.plan,
        item_category: 'plan',
        coupon: params.couponId,
        currency: params.currency,
        price: params.annualPrice,
        quantity: 1,
      }],
    },
  })
}

const handleActivationFailed = (params?: ActivationFailureParams) => {
  if (!params)
    return

  $intercom.call('trackEvent', 'activation-failed', {
    planPriceId: params.planPriceId,
    errorMessage: params.errorMessage,
  })
}

const now = useNow({ interval: 60_000 })
const isWorkspaceInRetentionPeriod = computed(() =>
  !!workspace.value
  && !!workspace.value.attributes.retention_starts_at
  && now.value.toISOString() >= workspace.value.attributes.retention_starts_at,
)
</script>

<template>
  <NuxtPage />
  <Toaster />
  <CookieConsent />
  <DTDialogContainer />
  <AppPausedCover v-if="subscriptionPausedCoverVisible" />
  <AppRetentionCover v-if="isWorkspaceInRetentionPeriod" />
  <SubscriptionModal
    v-if="isSubscribeModalOpen"
    v-model="isSubscribeModalOpen"
    @checkout-started="handleCheckoutStarted"
    @plan-selected="handlePlanSelected"
    @activation-completed="handleActivationCompleted"
    @activation-failed="handleActivationFailed"
    @show-intercom="$intercom.call('show')"
  />
</template>
